import React, { createContext, useState, useEffect, useContext } from "react";
import { AuthContext } from "../Auth/AuthContext";
import api from "../../services/api";
import toastError from "../../errors/toastError";

const PlanPermissionsContext = createContext();

export const PlanPermissionsProvider = ({ children }) => {
  const { user } = useContext(AuthContext);
  const [permissions, setPermissions] = useState({
    useWhatsapp: false,
    useFacebook: false,
    useInstagram: false,
    useCampaigns: false,
    useSchedules: false,
    useInternalChat: false,
    useExternalApi: false,
    useKanban: false,
    useOpenAi: false,
    useIntegrations: false,
    useWhatsappOfficial: false,
    useGroups: false,
    useLandingPages: false,
    useWebChat: false,
    useAssistants: false,
    useNapp: false,
    useAsterisk: false,
    useIASDR: false,
  });
  const [loading, setLoading] = useState(true);
  const [lastFetch, setLastFetch] = useState(null);

  // Cache de 5 minutos
  const CACHE_DURATION = 5 * 60 * 1000;

  const fetchPlanPermissions = async (force = false) => {
    if (!user || !user.companyId) {
      setLoading(false);
      return;
    }

    // Se não forçar e já buscou recentemente, não busca de novo
    if (!force && lastFetch && (Date.now() - lastFetch < CACHE_DURATION)) {
      console.log('📦 Usando permissões em cache');
      return;
    }

    try {
      const { data } = await api.get(`/companies/listPlan/${user.companyId}`);

      if (data && data.plan) {
        setPermissions({
          useWhatsapp: data.plan.useWhatsapp || false,
          useFacebook: data.plan.useFacebook || false,
          useInstagram: data.plan.useInstagram || false,
          useCampaigns: data.plan.useCampaigns || false,
          useSchedules: data.plan.useSchedules || false,
          useInternalChat: data.plan.useInternalChat || false,
          useExternalApi: data.plan.useExternalApi || false,
          useKanban: data.plan.useKanban || false,
          useOpenAi: data.plan.useOpenAi || false,
          useIntegrations: data.plan.useIntegrations || false,
          useWhatsappOfficial: data.plan.useWhatsappOfficial || false,
          useGroups: data.plan.useGroups || false,
          useLandingPages: data.plan.useLandingPages || false,
          useWebChat: data.plan.useWebChat || false,
          useAssistants: data.plan.useAssistants || false,
          useNapp: data.plan.useNapp || false,
          useAsterisk: data.plan.useAsterisk || false,
          useIASDR: data.plan.useIASDR || false,
        });
        setLastFetch(Date.now());
      }
    } catch (err) {
      console.error("Erro ao buscar permissões do plano:", err);
      toastError(err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchPlanPermissions();
  }, [user?.companyId]);

  // Função para verificar se uma funcionalidade específica está habilitada
  const hasPermission = (feature) => {
    return permissions[feature] || false;
  };

  // Função para verificar múltiplas permissões
  const hasAnyPermission = (features) => {
    return features.some(feature => permissions[feature]);
  };

  // Função para verificar se todas as permissões estão habilitadas
  const hasAllPermissions = (features) => {
    return features.every(feature => permissions[feature]);
  };

  // Função para forçar atualização das permissões
  const refreshPermissions = () => {
    return fetchPlanPermissions(true);
  };

  return (
    <PlanPermissionsContext.Provider
      value={{
        permissions,
        loading,
        hasPermission,
        hasAnyPermission,
        hasAllPermissions,
        refreshPermissions
      }}
    >
      {children}
    </PlanPermissionsContext.Provider>
  );
};

export default PlanPermissionsContext;
